import { kstDateOf, kstDateRangeDays } from '@shared/utils/kstTime.util';

import {
  BugoStatBrandRow,
  BugoStatDayRow,
  BugoStatDrillDown,
  BugoStatFevent,
  BugoStatFilters,
  BugoStatResult,
} from './adminBugoStat.types';
import { BrandNameResolver, defaultBrandNameResolver } from './analyticsBrandName';

//* ── 지표 정의 (백엔드 전용 API가 생겨도 이 정의를 그대로 따라야 한다) ──
//* 상가(fevent)   : 발인일이 조회 기간 안에 있는 fevent 1건
//* 공유           : meta.shareCount > 0
//*   ⚠️ 서버는 발인일이 지난 뒤의 공유는 세지 않는다(fevent.public.service.pure.ts addShareCount).
//*      따라서 과거 기간의 공유 수치는 "발인 전까지의 공유"만 반영한다.
//* 조의글 사용    : fevent.useAcqMsg === true (상가 단위 토글, 상조 단위 플래그가 아님)
//* 조의글 개수    : fevent.acqMsgs 중 isBan !== true (차단글은 화면에도 안 보이므로 제외).
//*                  "설정"은 켜기만 한 것이고 개수는 실제로 달린 글 수라 서로 다르다.
//*                  상조 고정 문구("OO 임직원 일동")는 프론트 표시용이라 DB에 없다 = 자동 제외.
//*                  평균은 전체 상가가 아니라 '글 달린 상가'로 나눈다 — 안 달린 곳까지
//*                  분모에 넣으면 평균이 희석돼 의미가 없다.
//* 부고(상주)     : fevent.bugos 각 건
//* 발송           : bugo.lastSentPhoneNumber가 비어있지 않음
//* isDeleted      : 발인 후 자동으로 붙는 개인정보 파기 플래그 — 절대 필터하지 않는다
//*                  (필터하면 과거 기간이 통째로 비어 보인다)

/** 그룹 키 — 멀티 브랜드 상조는 bugoAgencyList 항목별로 나눠 센다 */
export const brandKeyOf = (fevent: BugoStatFevent): string => {
  const brand = fevent.bugoBrand;
  const brandId = !brand ? '' : typeof brand === 'string' ? brand : brand._id;
  const index =
    typeof fevent.bugoBrandAgencyIndex === 'number' ? fevent.bugoBrandAgencyIndex : '';
  return `${brandId}#${index}`;
};

//* 이름은 해석기를 통해 얻는다 — 문서마다 bugoBrand를 populate하지 않기 위해서
//* (`useAnalyticsBrandNames`). 해석기를 안 주면 문서에 붙어온 값을 그대로 쓴다.
const brandNameOf = (
  fevent: BugoStatFevent,
  resolve: BrandNameResolver = defaultBrandNameResolver,
): string => resolve(fevent.bugoBrand, fevent.bugoBrandAgencyIndex);

const shareCountOf = (fevent: BugoStatFevent): number => {
  const raw = fevent.meta?.shareCount;
  return typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : 0;
};

const isSentBugo = (bugo: { lastSentPhoneNumber?: string | null }): boolean =>
  typeof bugo.lastSentPhoneNumber === 'string' && bugo.lastSentPhoneNumber.trim() !== '';

/**
 * 실제로 달린 조의글 수.
 * - 차단(isBan)된 글은 부고 화면에도 안 보이므로 세지 않는다.
 * - 부고 화면의 "{상조명} 임직원 일동" 고정 문구는 AcqMsgSection이 화면에서만 만들어
 *   붙이는 것(_id: '__brand__')이고 DB에는 없다. 그래도 혹시 저장되는 날이 오면
 *   조용히 수치가 부풀 자리라 명시적으로 걸러 둔다.
 */
export const BRAND_FIXED_ACQ_MSG_ID = '__brand__';

export const acqMsgCountOf = (fevent: BugoStatFevent): number =>
  (fevent.acqMsgs ?? []).filter(
    (msg) => msg.isBan !== true && msg._id !== BRAND_FIXED_ACQ_MSG_ID,
  ).length;

/** 발인일 → KST 'YYYY-MM-DD'. 날짜가 없거나 Invalid Date면 null */
export const coffinOutDayKey = (fevent: BugoStatFevent): string | null => {
  const raw = fevent.deceasedInfo?.coffinOut?.date;
  if (!raw) return null;
  const date = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  return kstDateOf(date);
};

/** 필터 통과 여부 */
export const matchesFilters = (
  fevent: BugoStatFevent,
  filters: BugoStatFilters,
): boolean => {
  if (filters.acqMsg === 'used' && fevent.useAcqMsg !== true) return false;
  if (filters.acqMsg === 'unused' && fevent.useAcqMsg === true) return false;

  const shared = shareCountOf(fevent) > 0;
  if (filters.share === 'shared' && !shared) return false;
  if (filters.share === 'unshared' && shared) return false;

  if (filters.brandKeys.length > 0 && !filters.brandKeys.includes(brandKeyOf(fevent))) {
    return false;
  }
  return true;
};

const emptyRow = (key: string, brandName: string): BugoStatBrandRow => ({
  key,
  brandName,
  feventCount: 0,
  sharedFeventCount: 0,
  unsharedFeventCount: 0,
  shareCountTotal: 0,
  acqMsgFeventCount: 0,
  acqMsgTotal: 0,
  acqMsgWrittenFeventCount: 0,
  bugoCount: 0,
  sentBugoCount: 0,
  unsentBugoCount: 0,
  unsentFeventCount: 0,
});

const addFeventToRow = (row: BugoStatBrandRow, fevent: BugoStatFevent): void => {
  const shareCount = shareCountOf(fevent);
  const bugos = fevent.bugos ?? [];
  const sent = bugos.filter(isSentBugo).length;

  row.feventCount += 1;
  if (shareCount > 0) row.sharedFeventCount += 1;
  else row.unsharedFeventCount += 1;
  row.shareCountTotal += shareCount;
  if (fevent.useAcqMsg === true) row.acqMsgFeventCount += 1;
  const acqMsgCount = acqMsgCountOf(fevent);
  row.acqMsgTotal += acqMsgCount;
  if (acqMsgCount > 0) row.acqMsgWrittenFeventCount += 1;
  row.bugoCount += bugos.length;
  row.sentBugoCount += sent;
  row.unsentBugoCount += bugos.length - sent;
  //* 부고가 아예 없는 상가는 "미발송 상가"로 세지 않는다 (보낼 대상 자체가 없음)
  if (bugos.length > 0 && sent === 0) row.unsentFeventCount += 1;
};

/** 합계 행에서 그룹 식별용 필드(key/brandName)를 떼어낸다 */
const totalsOf = (row: BugoStatBrandRow): BugoStatResult['totals'] => ({
  feventCount: row.feventCount,
  sharedFeventCount: row.sharedFeventCount,
  unsharedFeventCount: row.unsharedFeventCount,
  shareCountTotal: row.shareCountTotal,
  acqMsgFeventCount: row.acqMsgFeventCount,
  acqMsgTotal: row.acqMsgTotal,
  acqMsgWrittenFeventCount: row.acqMsgWrittenFeventCount,
  bugoCount: row.bugoCount,
  sentBugoCount: row.sentBugoCount,
  unsentBugoCount: row.unsentBugoCount,
  unsentFeventCount: row.unsentFeventCount,
});

/**
 * 상가 목록 → 상조별·일별 집계.
 * `days`는 조회 기간 전체를 빈 날 포함해 채운다(추이 차트가 끊기지 않도록).
 */
export const aggregateBugoStat = (
  fevents: BugoStatFevent[],
  filters: BugoStatFilters,
  period: { from: string; to: string },
  options?: {
    bugoDataUnavailable?: boolean;
    acqMsgDataUnavailable?: boolean;
    resolveBrandName?: BrandNameResolver;
  },
): BugoStatResult => {
  const brandMap = new Map<string, BugoStatBrandRow>();
  const dayMap = new Map<string, BugoStatDayRow>();
  const totals = emptyRow('', '');

  fevents.forEach((fevent) => {
    if (!matchesFilters(fevent, filters)) return;

    const key = brandKeyOf(fevent);
    let row = brandMap.get(key);
    if (!row) {
      row = emptyRow(key, brandNameOf(fevent, options?.resolveBrandName));
      brandMap.set(key, row);
    }
    addFeventToRow(row, fevent);
    addFeventToRow(totals, fevent);

    const dayKey = coffinOutDayKey(fevent);
    if (dayKey) {
      let day = dayMap.get(dayKey);
      if (!day) {
        day = { date: dayKey, feventCount: 0, sharedFeventCount: 0 };
        dayMap.set(dayKey, day);
      }
      day.feventCount += 1;
      if (shareCountOf(fevent) > 0) day.sharedFeventCount += 1;
    }
  });

  const brands = Array.from(brandMap.values()).sort(
    (a, b) => b.feventCount - a.feventCount || a.brandName.localeCompare(b.brandName),
  );

  //* 빈 날도 0으로 채워 추이가 끊기지 않게 한다
  const days: BugoStatDayRow[] = kstDateRangeDays(period.from, period.to).map(
    (date) => dayMap.get(date) ?? { date, feventCount: 0, sharedFeventCount: 0 },
  );

  return {
    totals: totalsOf(totals),
    brands,
    days,
    bugoDataUnavailable: options?.bugoDataUnavailable ?? false,
    acqMsgDataUnavailable: options?.acqMsgDataUnavailable ?? false,
  };
};

/**
 * 현재 필터 + 드릴다운 조건에 맞는 상가 목록.
 * 표/달력에서 "이 칸의 N건이 뭐였지"를 열어보는 용도라 집계와 같은 필터를 그대로 태운다.
 */
export const drillDownFevents = (
  fevents: BugoStatFevent[],
  filters: BugoStatFilters,
  drill: BugoStatDrillDown,
): BugoStatFevent[] =>
  fevents
    .filter((fevent) => {
      if (!matchesFilters(fevent, filters)) return false;
      if (drill.brandKey !== undefined && brandKeyOf(fevent) !== drill.brandKey) {
        return false;
      }
      if (drill.date !== undefined && coffinOutDayKey(fevent) !== drill.date) return false;
      if (drill.dateFrom !== undefined || drill.dateTo !== undefined) {
        const day = coffinOutDayKey(fevent);
        //* 발인일이 없는 상가는 구간에 넣을 근거가 없다
        if (!day) return false;
        if (drill.dateFrom !== undefined && day < drill.dateFrom) return false;
        if (drill.dateTo !== undefined && day > drill.dateTo) return false;
      }
      return true;
    })
    //* 발인일 오름차순 → 같은 날은 고인명순
    .sort((a, b) => {
      const dayDiff = (coffinOutDayKey(a) ?? '').localeCompare(coffinOutDayKey(b) ?? '');
      if (dayDiff !== 0) return dayDiff;
      return (a.deceasedInfo?.name ?? '').localeCompare(b.deceasedInfo?.name ?? '');
    });

/** 목록에 쓸 장례식장 표기 — populate 우선, 없으면 embed */
export const funeralHomeNameOf = (fevent: BugoStatFevent): string => {
  const populated = fevent.funeralHomeInfo;
  if (populated && typeof populated !== 'string' && populated.name) return populated.name;
  return fevent.funeralHomeInfoEmbed?.name ?? '';
};

/** 목록에 쓸 상조 표기 */
export const brandNameOfFevent = (
  fevent: BugoStatFevent,
  resolve?: BrandNameResolver,
): string => brandNameOf(fevent, resolve);

/** 발송된 부고 수 / 전체 부고 수 */
export const bugoSentCountOf = (
  fevent: BugoStatFevent,
): { total: number; sent: number } => {
  const bugos = fevent.bugos ?? [];
  return { total: bugos.length, sent: bugos.filter(isSentBugo).length };
};

/** 0으로 나누기 없이 비율(%) — 분모 0이면 null */
export const rateOf = (numerator: number, denominator: number): number | null =>
  denominator > 0 ? (numerator / denominator) * 100 : null;

/** 비율 표기. 분모가 0이면 '-' */
export const formatRate = (numerator: number, denominator: number): string => {
  const rate = rateOf(numerator, denominator);
  return rate === null ? '-' : `${rate.toFixed(1)}%`;
};
